import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Autocomplete,
  CircularProgress,
  Grid,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { useNavigate, useParams } from "react-router-dom";
import { useGetApi } from "../hooks/useGetApi";
import { getUnitApi } from "../services/unit.service";
import {
  getRoleApi,
  getUserApi,
  getUserByIdApi,
  updateRole,
  updateUnit,
} from "../services/user.service";
import { getDepartmentApi } from "../services/department.service";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const UserUnitRoleChange = () => {
  const navigate = useNavigate();
  const { id, type } = useParams();
  const changeRole = type === "change-role";
  const changeUnit = type === "change-unit";

  const initialState = {
    ...(changeRole ? { role: null } : { unit: null }),
    requestedBy: null,
    authorizedBy: null,
    approvalDate: null,
    effectiveDate: dayjs(),
    reasonForChange: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowLoading, setSelectedRowLoading] = useState(false);
  const [selectedRowError, setSelectedRowError] = useState(false);

  // Fetch user data when id changes
  useEffect(() => {
    const fetchUserData = async () => {
      if (!id) {
        setSelectedRow(null);
        return;
      }
      
      setSelectedRowLoading(true);
      setSelectedRowError(false);
      
      try {
        const response = await getUserByIdApi({ id });
        console.log('UserUnitRoleChange API Response:', response);
        
        if (response?.code === 200) {
          setSelectedRow(response.data);
        } else {
          setSelectedRowError(true);
          toast.error(response?.message || "Failed to fetch user data");
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        setSelectedRowError(true);
        toast.error("Failed to fetch user data");
      } finally {
        setSelectedRowLoading(false);
      }
    };

    fetchUserData();
  }, [id]);

  const { dataList: unitList } = useGetApi({
    apiFunction: getUnitApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "units",
  });
  const { dataList: roleList } = useGetApi({
    apiFunction: getRoleApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
  });
  const { dataList: departmentList } = useGetApi({
    apiFunction: getDepartmentApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
  });
  const { dataList: userList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((preValue) => ({
      ...preValue,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedRow?._id) return;

    let response;

    const formDataWithId = {
      ...formData,
      department: formData?.department?._id,
      role: formData?.role?._id,
      unit: formData?.unit?._id,
      requestedBy: formData?.requestedBy?._id,
      authorizedBy: formData?.authorizedBy?._id,
    };

    setIsLoading(true);
    if (changeRole) {
      response = await updateRole({ id: selectedRow?._id }, formDataWithId);
    } else {
      response = await updateUnit({ id: selectedRow?._id }, formDataWithId);
    }
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        response?.message ||
          `${
            selectedRow?._id && changeRole ? "Role" : "Unit"
          } updated successfully`
      );
      navigate(-1);
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  useEffect(() => {
    if (selectedRow?._id) {
      setFormData({
        ...initialState,
        ...(changeRole
          ? { role: selectedRow?.role || null }
          : { unit: selectedRow?.unit || null }),
        requestedBy: selectedRow?.requestedBy || null,
        authorizedBy: selectedRow?.authorizedBy || null,
        approvalDate: selectedRow?.approvalDate || null,
        effectiveDate: selectedRow?.effectiveDate || null,
        reasonForChange: selectedRow?.reasonForChange || "",
      });
    } else {
      setFormData(initialState);
    }
  }, [id, selectedRow]);

  useEffect(() => {
    if (!changeRole && !changeUnit) {
      navigate(-1);
    }
  }, []);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        {selectedRowLoading ? (
          <Loader />
        ) : selectedRowError ? (
          <ErrorMessage />
        ) : (
          <Box sx={{ width: "100%" }}>
            {/* Form */}

            <Box
              sx={{
                padding: 2,
                borderRadius: 2,
                marginTop: 2,
                marginRight: "90px",
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  color: '#5D6A82', 
                  textTransform: 'none', 
                  fontSize: '16px',
                  fontWeight: 'bold',
                  padding: 0,
                  transition: 'none !important',
                  mb: 2
                }}
              >
                Back
              </Button>
              {/* User Form */}
              <p
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  marginBottom: "1rem",
                }}
              >
                {`Update ${changeRole ? "Role" : "Unit"}`}
              </p>

              {/* Personal Information */}
              <Box
                sx={{
                  borderRadius: 2,
                  marginBottom: 2,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: "20rem",
                    display: "flex",
                    flexDirection: "column",
                    align: "center",
                    justifyContent: "space-evenly",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "15rem",
                      border: "1px solid black",
                      borderRadius: 2,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    <Box
                      component="img"
                      src={selectedRow?.profilePicture}
                      alt="User Profile"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    ></Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    backgroundColor: "#f5faff",
                    padding: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle1" marginBottom={2} color="grey">
                    Personal Information
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ display: "flex", gap: 1, flex: "1 1 Calc(50% - 8px)" }}>
                      <TextField
                        label="Title"
                        sx={{ minWidth: 80 }}
                        value={selectedRow?.title || ""}
                        disabled
                        size="small"
                      />
                      <TextField
                        label="First Name"
                        sx={{ flex: 1 }}
                        value={selectedRow?.firstName || ""}
                        onChange={handleInputChange}
                        name="firstName"
                        disabled
                      />
                    </Box>
                    <TextField
                      label="Last Name"
                      sx={{ flex: "1 1 Calc(50% - 8px)" }}
                      value={selectedRow?.lastName || ""}
                      onChange={handleInputChange}
                      name="lastName"
                      disabled
                    />
                    <TextField
                      label="Email"
                      type="email"
                      sx={{ flex: "1 1 Calc(50% - 8px)" }}
                      value={selectedRow?.email || ""}
                      onChange={handleInputChange}
                      name="email"
                      disabled
                    />
                    <TextField
                      label="Contact Number"
                      sx={{ flex: "1 1 Calc(50% - 8px)" }}
                      value={selectedRow?.contactNo || ""}
                      onChange={handleInputChange}
                      name="contactNo"
                      disabled
                    />
                    <DatePicker
                      label="DOB"
                      slotProps={{ textField: { fullWidth: true } }}
                      value={selectedRow?.dob ? dayjs(selectedRow.dob) : null}
                      onChange={(newValue) =>
                        handleInputChange({
                          target: { name: "dob", value: newValue },
                        })
                      }
                      sx={{ flex: "1 1 Calc(50% - 8px)" }}
                      disabled
                    />
                    <FormControl sx={{ flex: "1 1 Calc(50% - 8px)" }} disabled>
                      <InputLabel
                        sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                      >
                        Gender
                      </InputLabel>
                      <Select
                        value={selectedRow?.gender || ""}
                        onChange={handleInputChange}
                        name="gender"
                      >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: "1 1 Calc(50% - 8px)" }} disabled>
                      <InputLabel
                        sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                      >
                        Select Identification Proof
                      </InputLabel>
                      <Select
                        value={selectedRow?.identityProof || ""}
                        onChange={handleInputChange}
                        name="identityProof"
                      >
                        <MenuItem value="aadhar">Aadhar</MenuItem>
                        <MenuItem value="pan">Pan</MenuItem>
                        <MenuItem value="passport">Passport</MenuItem>
                        <MenuItem value="drivingLicense">
                          Driving License
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label={
                        {
                          aadhar: "Aadhar Number",
                          pan: "PAN Number",
                          passport: "Passport Number",
                          drivingLicense: "Driving License Number",
                        }[selectedRow?.identityProof] || "Identification Number"
                      }
                      sx={{ flex: "1 1 Calc(50% - 8px)" }}
                      value={selectedRow?.identityNo}
                      onChange={handleInputChange}
                      name="identityNo"
                      disabled
                    />
                  </Box>
                </Box>
              </Box>

              {/* Professional Information */}
              <Box
                sx={{
                  padding: 2,
                  backgroundColor: "#f5faff",
                  borderRadius: 2,
                  marginBottom: 2,
                }}
              >
                <Typography variant="subtitle1" color="grey" marginBottom={2}>
                  New Role and Unit
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={unitList || []}
                      getOptionLabel={(option) => option?.unitName || ""}
                      renderInput={(params) => (
                        <TextField {...params} label="Unit" />
                      )}
                      value={formData?.unit || selectedRow?.unit || null}
                      onChange={(_, newValue) =>
                        handleInputChange({
                          target: { name: "unit", value: newValue },
                        })
                      }
                      disabled={!changeUnit && selectedRow?._id}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={departmentList || []}
                      getOptionLabel={(option) => option?.name || ""}
                      renderInput={(params) => (
                        <TextField {...params} label="Department" />
                      )}
                      value={selectedRow?.department || null}
                      onChange={(_, newValue) =>
                        handleInputChange({
                          target: { name: "department", value: newValue },
                        })
                      }
                      disabled={selectedRow?._id}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={roleList || []}
                      getOptionLabel={(option) => option?.role || ""}
                      renderInput={(params) => (
                        <TextField {...params} label="Role" />
                      )}
                      value={formData?.role || selectedRow?.role || null}
                      onChange={(_, newValue) =>
                        handleInputChange({
                          target: { name: "role", value: newValue },
                        })
                      }
                      disabled={!changeRole && selectedRow?._id}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <DatePicker
                      label="Effective Date"
                      slotProps={{ textField: { fullWidth: true } }}
                      value={
                        formData?.effectiveDate
                          ? dayjs(formData.effectiveDate)
                          : null
                      }
                      onChange={(newValue) =>
                        handleInputChange({
                          target: { name: "effectiveDate", value: newValue },
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label={`Reason for ${
                        changeUnit ? "Unit" : "Role"
                      } Change`}
                      fullWidth
                      value={formData?.reasonForChange}
                      onChange={handleInputChange}
                      name="reasonForChange"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Approval and Authorization */}
              <Box
                sx={{
                  padding: 2,
                  backgroundColor: "#f5faff",
                  borderRadius: 2,
                  marginBottom: 2,
                }}
              >
                <Typography variant="subtitle1" color="grey" marginBottom={2}>
                  Approval and Authorization
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={userList || []}
                      getOptionLabel={(option) =>
                        `${option?.firstName || ""} ${
                          option?.lastName || ""
                        } (${option?.userId})`
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Requested By" />
                      )}
                      value={formData?.requestedBy || null}
                      onChange={(_, newValue) =>
                        handleInputChange({
                          target: { name: "requestedBy", value: newValue },
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      options={userList || []}
                      getOptionLabel={(option) =>
                        `${option?.firstName || ""} ${
                          option?.lastName || ""
                        } (${option?.userId})`
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Authorized By" />
                      )}
                      value={formData?.authorizedBy || null}
                      onChange={(_, newValue) =>
                        handleInputChange({
                          target: { name: "authorizedBy", value: newValue },
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <DatePicker
                      label="Approval Date"
                      slotProps={{ textField: { fullWidth: true } }}
                      value={
                        formData?.approvalDate
                          ? dayjs(formData.approvalDate)
                          : null
                      }
                      onChange={(newValue) =>
                        handleInputChange({
                          target: { name: "approvalDate", value: newValue },
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Save Button */}
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ padding: "10px 40px", textTransform: "none" }}
                  onClick={handleSave}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : "Save Changes"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default UserUnitRoleChange;
